import React from "react"
import {
  Box, Divider, Flex, Skeleton, Stack, Text,
} from "@chakra-ui/react"
import Button from "components/Buttons/Button"
import { TextDeliveryCounts, TextMessageDeliveryCounts } from "sharedTypes"
import { LOW_SMS_CREDIT_THRESHOLD } from "utilities/constants"
import DashboardCard, { LinkAction } from "../components/DashboardCard"
import { Bar } from "../components/charts"

export type UsageRow = {
  label: string
  used: number
  limit?: number | null
  unit: string
  remaining?: boolean
  loading?: boolean
  color?: string
  action?: { label: string, onClick: () => void }
}

const SENT: (keyof TextMessageDeliveryCounts)[] = [
  "queued", "sent", "delivered", "undelivered", "failed", "deliveryUnknown",
]

// Every text the event sent spends one credit, SMS or WhatsApp; texts held for an opt-in don't.
// The API's creditsUsed also counts SMS fallbacks; the sum is for an API without it.
export const creditsUsed = ({ creditsUsed: fromApi, sms, whatsapp }: TextDeliveryCounts) => (
  fromApi ?? [sms, whatsapp].reduce((total, channel) => (
    total + SENT.reduce((sum, status) => sum + (channel?.[status] ?? 0), 0)
  ), 0)
)

const balanceColor = (left: number) => {
  if (left <= 0) return "red.500"
  if (left < LOW_SMS_CREDIT_THRESHOLD) return "orange.500"

  return "blue.500"
}

type CreditsRowOptions = { used?: number, left: number, loading?: boolean, onBuy?: () => void }

// Credits belong to the account: this event's use, out of that plus what's left.
export const creditsRow = ({
  used, left, loading, onBuy,
}: CreditsRowOptions): UsageRow => ({
  label: "Text Message Credits",
  unit: "credits",
  ...used === undefined && !loading
    ? { used: left, remaining: true }
    : {
      used: used ?? 0, limit: (used ?? 0) + left, loading, color: balanceColor(left),
    },
  ...onBuy && left < LOW_SMS_CREDIT_THRESHOLD && { action: { label: "Buy credits", onClick: onBuy } },
})

export const nearLimit = (used: number, limit?: number | null) => (
  Boolean(limit) && used >= (limit ?? 0) * 0.8
)

export const usageColor = (used: number, limit: number) => {
  if (used >= limit) return "red.500"
  if (nearLimit(used, limit)) return "orange.500"

  return "blue.500"
}

type Props = {
  rows: UsageRow[]
  planName?: string
  missingFeatures: string[]
  canUpgrade: boolean
  onUpgrade: () => void
}

const UsageCard = ({
  rows, planName, missingFeatures, canUpgrade, onUpgrade,
}: Props) => (
  <DashboardCard title="Event Usage">
    <Stack spacing={6}>
      {rows.map(({
        label, used, limit, unit, remaining, loading, color, action,
      }) => {
        const near = nearLimit(used, limit)

        return (
          <Box key={label}>
            <Flex justify="space-between" align="baseline" gap={2} mb={2}>
              <Text color="gray.700" noOfLines={1}>{label}</Text>
              {loading ? <Skeleton h={4} w={20} aria-label={`Loading ${label}`} /> : (
                <Text fontWeight="semibold" flexShrink={0}>
                  {used.toLocaleString()}
                  {remaining && <Text as="span" color="gray.600" fontWeight="normal"> left</Text>}
                  {limit ? <Text as="span" color="gray.600" fontWeight="normal"> / {limit.toLocaleString()}</Text> : null}
                </Text>
              )}
            </Flex>
            {limit ? <Bar pct={loading ? 0 : (used / limit) * 100} color={color ?? usageColor(used, limit)} track="gray.200" /> : null}
            {action && (
              <Box mt={1.5}>
                <LinkAction onClick={action.onClick}>{action.label}</LinkAction>
              </Box>
            )}
            {!action && near && canUpgrade && (
              <Box mt={1.5}>
                <LinkAction onClick={onUpgrade}>Upgrade for more {unit}</LinkAction>
              </Box>
            )}
          </Box>
        )
      })}
    </Stack>
    {(planName || canUpgrade) && <Divider my={5} />}
    {planName && <Text fontWeight="semibold">{planName} Plan</Text>}
    {missingFeatures.length > 0 && (
      <Text fontSize="xs" color="gray.600" mt={1}>
        Not on your plan: {missingFeatures.slice(0, 3).join(", ")}
        {missingFeatures.length > 3 && <Text as="span" color="gray.600"> +{missingFeatures.length - 3}</Text>}
      </Text>
    )}
    {canUpgrade && (
      <Button w="full" mt={4} onClick={onUpgrade}>Compare plans & upgrade</Button>
    )}
  </DashboardCard>
)

export default UsageCard
